/**
 * House video player (ads epic) — plays bundled house video creatives
 * (HOUSE_VIDEO_ADS) full-screen for the interstitial and rewarded formats.
 *
 * This is what un-stubs the house provider's showInterstitial/showRewarded:
 * a real <video> in a full-screen overlay above the play layer (z-index 200),
 * with the two modes differing only in policy:
 *
 *   INTERSTITIAL — skippable after SKIP_AFTER_S of visible playback; closing
 *     early still counts as "shown" (the impression fired on play). Between-
 *     runs filler, no reward.
 *   REWARDED — the reward is earned ONLY by watching to the end (the video's
 *     `ended` event). Skipping/closing early resolves 'dismissed' and grants
 *     nothing — the manager maps 'completed' → the server token grant.
 *
 * The overlay lives in shell chrome, never inside a game iframe. Audio
 * respects the cabinet mute toggle. One player at a time (a module singleton
 * guards against a double-open racing two overlays).
 */

import { recordAdEvent } from '../api/client.js';
import { getCabinetMuted } from '../cabinet/mute.js';
import { HOUSE_VIDEO_ADS, type HouseVideoAd } from './house-provider.js';
import { HOUSE_PROVIDER_ID } from './house-provider.js';
import type { AdContext } from './provider.js';

/** How long into an interstitial before the SKIP control appears. */
const SKIP_AFTER_S = 5;

/** Weighted pick avoiding the last-shown clip when possible (varied rotation). */
let lastVideoId: string | null = null;
function pickVideo(): HouseVideoAd | null {
  if (HOUSE_VIDEO_ADS.length === 0) return null;
  const pool = HOUSE_VIDEO_ADS.filter((v) => v.id !== lastVideoId);
  const from = pool.length > 0 ? pool : HOUSE_VIDEO_ADS;
  const total = from.reduce((s, v) => s + v.weight, 0);
  let r = Math.random() * total;
  for (const v of from) {
    r -= v.weight;
    if (r <= 0) return v;
  }
  return from[from.length - 1]!;
}

/** True when a house video overlay is already on screen (prevents double-open). */
let playing = false;

export type HouseVideoMode = 'interstitial' | 'rewarded';

/** Why a play() resolved — mirrors the provider result statuses. */
export type HouseVideoOutcome = 'completed' | 'dismissed' | 'unavailable' | 'upgrade';

/**
 * Play one house video creative full-screen.
 *
 * Resolves:
 *   'completed'   — the clip played to its end (rewarded: earns the token).
 *   'dismissed'   — the viewer skipped/closed before the end.
 *   'unavailable' — no creative, or the <video> errored / couldn't start.
 *   'upgrade'     — the viewer tapped the "Remove ads" upsell; the caller
 *                   should open the paywall (interstitial only).
 *
 * Never rejects — an ad failure is never the player's problem.
 */
export function playHouseVideo(mode: HouseVideoMode, ctx: AdContext): Promise<HouseVideoOutcome> {
  if (playing) return Promise.resolve('unavailable');
  const ad = pickVideo();
  if (!ad) return Promise.resolve('unavailable');

  playing = true;
  lastVideoId = ad.id;

  return new Promise<HouseVideoOutcome>((resolve) => {
    let settled = false;
    let skipTimer = 0;

    // --- overlay scaffold ---------------------------------------------------
    const overlay = document.createElement('div');
    overlay.className = 'house-ad-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Sponsor video');

    const video = document.createElement('video');
    video.className = 'house-ad-overlay__video';
    video.src = ad.src;
    video.playsInline = true;
    video.muted = getCabinetMuted();
    video.preload = 'auto';
    video.controls = false;
    overlay.appendChild(video);

    // Click-through to the advertiser (counts a click, opens in a new tab).
    const cta = document.createElement('a');
    cta.className = 'house-ad-overlay__cta';
    cta.href = ad.href;
    cta.target = '_blank';
    cta.rel = 'noopener noreferrer';
    cta.textContent = 'Learn more →';
    cta.addEventListener('click', () => {
      recordAdEvent({
        slot: ctx.slot,
        adId: ad.id,
        event: 'click',
        provider: HOUSE_PROVIDER_ID,
        format: mode,
        cartridgeId: ctx.cartridgeId ?? undefined,
        deviceId: ctx.deviceId,
      });
    });
    overlay.appendChild(cta);

    // "Remove ads — upgrade" upsell (INTERSTITIAL only). Every interstitial is
    // captured attention on an unpaid user (entitled players never see ads),
    // so it doubles as an upgrade surface. Tapping it resolves 'upgrade' and
    // the caller opens the paywall. Shown immediately — the pitch should be
    // visible for the whole ad, not gated behind the skip timer.
    let upsell: HTMLButtonElement | null = null;
    if (mode === 'interstitial') {
      upsell = document.createElement('button');
      upsell.type = 'button';
      upsell.className = 'house-ad-overlay__upsell';
      upsell.textContent = '✦ Remove ads — upgrade';
      overlay.appendChild(upsell);
    }

    // Skip / close control. For rewarded it's a "no-reward" bail; for
    // interstitial it's a normal skip. Hidden until SKIP_AFTER_S for
    // interstitial; for rewarded it's shown but labelled as forfeiting.
    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'house-ad-overlay__skip';
    skip.textContent = mode === 'rewarded' ? 'Skip (no reward)' : 'Skip';
    skip.hidden = mode === 'interstitial'; // reveal after the timer below
    overlay.appendChild(skip);

    document.body.appendChild(overlay);

    // --- teardown + settle (exactly once) -----------------------------------
    const finish = (outcome: HouseVideoOutcome): void => {
      if (settled) return;
      settled = true;
      if (skipTimer) window.clearTimeout(skipTimer);
      video.pause();
      video.removeAttribute('src');
      video.load();
      overlay.remove();
      playing = false;
      resolve(outcome);
    };

    // Impression fires once playback begins; 'complete'/'skip' are gated on it
    // so a video that never started doesn't distort the completion rate.
    let impressionLogged = false;

    // --- funnel event helper ------------------------------------------------
    const emit = (event: 'complete' | 'skip'): void => {
      recordAdEvent({
        slot: ctx.slot,
        adId: ad.id,
        event,
        provider: HOUSE_PROVIDER_ID,
        format: mode,
        cartridgeId: ctx.cartridgeId ?? undefined,
        deviceId: ctx.deviceId,
      });
    };

    // --- events -------------------------------------------------------------
    // 'complete'/'skip' fire only if the impression logged (playback started),
    // so a video that never played doesn't pollute the completion rate.
    video.addEventListener('ended', () => {
      if (impressionLogged) emit('complete');
      finish('completed');
    });
    video.addEventListener('error', () => finish('unavailable'));
    skip.addEventListener('click', () => {
      if (impressionLogged) emit('skip');
      finish('dismissed');
    });

    // Upsell tap → log a click (distinct adId so upgrade-CTA pull is
    // measurable separately from the advertiser click) and resolve 'upgrade'
    // so the caller opens the paywall.
    upsell?.addEventListener('click', () => {
      recordAdEvent({
        slot: ctx.slot,
        adId: 'remove-ads-upsell',
        event: 'click',
        provider: HOUSE_PROVIDER_ID,
        format: mode,
        cartridgeId: ctx.cartridgeId ?? undefined,
        deviceId: ctx.deviceId,
      });
      finish('upgrade');
    });

    // Impression fires once playback actually begins (viewable), tagged with
    // the real format so house video CTR/fill splits out in the DB.
    video.addEventListener('playing', () => {
      if (impressionLogged) return;
      impressionLogged = true;
      recordAdEvent({
        slot: ctx.slot,
        adId: ad.id,
        event: 'impression',
        provider: HOUSE_PROVIDER_ID,
        format: mode,
        cartridgeId: ctx.cartridgeId ?? undefined,
        deviceId: ctx.deviceId,
      });
    });

    // Interstitial: reveal SKIP after the grace window (visible-time-ish —
    // a plain timeout is fine here; the clip is short and foregrounded).
    if (mode === 'interstitial') {
      skipTimer = window.setTimeout(() => {
        skip.hidden = false;
      }, SKIP_AFTER_S * 1000);
    }

    // Kick playback. Autoplay-with-sound may be blocked; retry muted so the
    // ad still runs (audio then follows the cabinet mute state on next play).
    void video.play().catch(() => {
      video.muted = true;
      void video.play().catch(() => finish('unavailable'));
    });
  });
}
